/**
 * Script de blocage des trackers
 * Empêche le chargement de Google Analytics et Meta Pixel tant que l'utilisateur n'a pas donné son consentement
 */
class TrackingBlocker {
  constructor() {
    this.consentKey = 'consent';
    this.blockedScripts = [];
    this.blockedFunctions = [];
    
    this.init();
  }

  init() {
    // Vérifier le consentement actuel
    this.checkConsent();
    
    // Intercepter les nouveaux scripts
    this.interceptScripts();
    
    // Intercepter les fonctions de tracking
    this.interceptTrackingFunctions();
    
    // Écouter les changements de consentement
    this.listenForConsentChanges();
  }

  checkConsent() {
    try {
      const consent = localStorage.getItem(this.consentKey);
      return consent === 'accepted';
    } catch (error) {
      console.warn('Erreur lors de la lecture du consentement:', error);
      return false;
    }
  }

  interceptScripts() {
    // Sauvegarder la fonction originale createElement
    const originalCreateElement = document.createElement;
    
    document.createElement = function(tagName) {
      const element = originalCreateElement.call(this, tagName);
      
      // Intercepter les scripts de tracking
      if (tagName.toLowerCase() === 'script') {
        const originalSrcSetter = Object.getOwnPropertyDescriptor(HTMLScriptElement.prototype, 'src').set;
        const originalSetAttribute = element.setAttribute;
        
        // Intercepter la propriété src
        Object.defineProperty(element, 'src', {
          set: function(value) {
            if (this.shouldBlockScript(value)) {
              console.log('Script bloqué (attente du consentement):', value);
              this._blockedSrc = value;
              return;
            }
            originalSrcSetter.call(this, value);
          },
          get: function() {
            return this._blockedSrc || '';
          }
        });
        
        // Intercepter setAttribute pour src
        element.setAttribute = function(name, value) {
          if (name.toLowerCase() === 'src' && this.shouldBlockScript(value)) {
            console.log('Script bloqué via setAttribute (attente du consentement):', value);
            this._blockedSrc = value;
            return;
          }
          originalSetAttribute.call(this, name, value);
        };
        
        // Intercepter le contenu du script
        const originalTextContentSetter = Object.getOwnPropertyDescriptor(Node.prototype, 'textContent').set;
        Object.defineProperty(element, 'textContent', {
          set: function(value) {
            if (this.shouldBlockInlineScript(value)) {
              console.log('Script inline bloqué (attente du consentement)');
              this._blockedContent = value;
              return;
            }
            originalTextContentSetter.call(this, value);
          },
          get: function() {
            return this._blockedContent || '';
          }
        });
      }
      
      return element;
    };
  }

  shouldBlockScript(src) {
    if (!this.checkConsent()) {
      const blockedPatterns = [
        /google-analytics\.com/,
        /googletagmanager\.com/,
        /googleadservices\.com/,
        /googlesyndication\.com/,
        /facebook\.net/,
        /connect\.facebook\.net/,
        /analytics\.google\.com/,
        /gtag\.js/,
        /ga\.js/,
        /google-analytics\.js/,
        /fbevents\.js/,
        /pixel\.js/
      ];
      
      return blockedPatterns.some(pattern => pattern.test(src));
    }
    return false;
  }

  shouldBlockInlineScript(content) {
    if (!this.checkConsent()) {
      const blockedPatterns = [
        /gtag\(/,
        /ga\(/,
        /fbq\(/,
        /dataLayer/,
        /google_tag_manager/,
        /GoogleAnalytics/,
        /FacebookPixel/
      ];
      
      return blockedPatterns.some(pattern => pattern.test(content));
    }
    return false;
  }

  interceptTrackingFunctions() {
    // Bloquer gtag
    if (!window.gtag) {
      window.gtag = function() {
        if (!this.checkConsent()) {
          console.log('gtag() bloqué (attente du consentement)');
          this.blockedFunctions.push({ fn: 'gtag', args: arguments });
          return;
        }
        // Si le consentement est donné, on peut exécuter gtag normalement
        // mais il faut d'abord charger le script
        this.loadTrackingScripts();
      }.bind(this);
    }

    // Bloquer ga
    if (!window.ga) {
      window.ga = function() {
        if (!this.checkConsent()) {
          console.log('ga() bloqué (attente du consentement)');
          this.blockedFunctions.push({ fn: 'ga', args: arguments });
          return;
        }
        this.loadTrackingScripts();
      }.bind(this);
    }

    // Bloquer fbq
    if (!window.fbq) {
      window.fbq = function() {
        if (!this.checkConsent()) {
          console.log('fbq() bloqué (attente du consentement)');
          this.blockedFunctions.push({ fn: 'fbq', args: arguments });
          return;
        }
        this.loadTrackingScripts();
      }.bind(this);
    }

    // Bloquer dataLayer
    if (!window.dataLayer) {
      window.dataLayer = [];
      const originalPush = window.dataLayer.push;
      window.dataLayer.push = function() {
        if (!this.checkConsent()) {
          console.log('dataLayer.push() bloqué (attente du consentement)');
          this.blockedFunctions.push({ fn: 'dataLayer.push', args: arguments });
          return;
        }
        originalPush.apply(this, arguments);
      }.bind(this);
    }
  }

  listenForConsentChanges() {
    // Écouter l'événement personnalisé du bandeau de consentement
    document.addEventListener('consent-banner-choice', (event) => {
      if (event.detail.choice === 'accepted') {
        this.onConsentAccepted();
      } else {
        this.onConsentDeclined();
      }
    });

    // Vérifier périodiquement les changements de localStorage
    setInterval(() => {
      if (this.checkConsent() && this.blockedScripts.length > 0) {
        this.onConsentAccepted();
      }
    }, 1000);
  }

  onConsentAccepted() {
    console.log('Consentement accepté - Chargement des scripts de tracking');
    
    // Charger les scripts bloqués
    this.loadBlockedScripts();
    
    // Exécuter les fonctions bloquées
    this.executeBlockedFunctions();
    
    // Nettoyer les listes
    this.blockedScripts = [];
    this.blockedFunctions = [];
  }

  onConsentDeclined() {
    console.log('Consentement refusé - Scripts de tracking bloqués définitivement');
    // Nettoyer les listes sans les exécuter
    this.blockedScripts = [];
    this.blockedFunctions = [];
  }

  loadBlockedScripts() {
    // Charger Google Analytics si nécessaire
    if (!document.querySelector('script[src*="googletagmanager.com"]')) {
      this.loadGoogleAnalytics();
    }
    
    // Charger Facebook Pixel si nécessaire
    if (!document.querySelector('script[src*="facebook.net"]')) {
      this.loadFacebookPixel();
    }
  }

  loadGoogleAnalytics() {
    // Créer et charger le script Google Analytics
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=GA_MEASUREMENT_ID';
    document.head.appendChild(script);
    
    // Initialiser gtag
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    window.gtag = gtag;
    gtag('js', new Date());
  }

  loadFacebookPixel() {
    // Créer et charger le script Facebook Pixel
    const script = document.createElement('script');
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
    `;
    document.head.appendChild(script);
  }

  executeBlockedFunctions() {
    this.blockedFunctions.forEach(item => {
      console.log(`Exécution de la fonction bloquée: ${item.fn}`);
      try {
        switch (item.fn) {
          case 'gtag':
            if (window.gtag && typeof window.gtag === 'function') {
              window.gtag.apply(window, item.args);
            }
            break;
          case 'ga':
            if (window.ga && typeof window.ga === 'function') {
              window.ga.apply(window, item.args);
            }
            break;
          case 'fbq':
            if (window.fbq && typeof window.fbq === 'function') {
              window.fbq.apply(window, item.args);
            }
            break;
          case 'dataLayer.push':
            if (window.dataLayer && Array.isArray(window.dataLayer)) {
              window.dataLayer.push.apply(window.dataLayer, item.args);
            }
            break;
        }
      } catch (error) {
        console.error(`Erreur lors de l'exécution de ${item.fn}:`, error);
      }
    });
  }

  // Méthode publique pour forcer le chargement des scripts (utile pour les tests)
  forceLoadTrackingScripts() {
    this.onConsentAccepted();
  }
}

// Initialiser le blocage des trackers immédiatement
window.trackingBlocker = new TrackingBlocker();

// Exposer la classe globalement
window.TrackingBlocker = TrackingBlocker;
